import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OutputCard } from '@/modules/outputs/components/OutputCard';
import { PreviewLayover } from '@/modules/outputs/components/PreviewLayover';
import { EOutputCardSize, type TResolvedValue } from '@/modules/outputs/types';
import {
  applyDisplayFilters,
  extractOutputsValue,
} from '@/modules/outputs/utils';
import { downloadOutput } from '@/modules/outputs/utils/download';
import type { V1NodeExecutedOutputs } from '@/generated/hooks/bishop/models';
import { useGetExecutionOutputs } from '@/generated/hooks/jarvis/v1/api';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Flex,
  Spinner,
  Text,
} from '@kognitos/lattice';
import { captureException } from '@/modules/sentry';
import { checkIsAccessDeniedError } from '@/shared/utils/error-handler';
import { AccessDeniedAlert } from '@/shared/components';
import {
  useUserPreferencesStore,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
} from '@/shared/stores';

/**
 * Props for QuillChatOutputRenderer component.
 */
interface IChatOutputRendererProps {
  executionId: string;
}

export function ChatOutputRenderer({ executionId }: IChatOutputRendererProps) {
  const { t } = useTranslation('chat');
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const [previewData, setPreviewData] = useState<{
    name: string;
    value: TResolvedValue;
  } | null>(null);

  /**
   * Fetches execution outputs from jarvis API using generated hook.
   */
  const {
    data: responseData,
    isLoading,
    isError,
    error: executionOutputsError,
  } = useGetExecutionOutputs(
    currentOrganizationId,
    currentWorkspaceId,
    executionId,
    {
      query: {
        enabled: Boolean(
          executionId && currentOrganizationId && currentWorkspaceId,
        ),
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        meta: {
          onError: (error: Error) => {
            captureException(error, {
              tags: { module: 'automation', action: 'fetchExecutionOutputs' },
            });
          },
        },
      },
    },
  );

  /**
   * Extracts outputs from response.
   */
  const outputsData = useMemo((): V1NodeExecutedOutputs | null => {
    if (!responseData?.data?.outputs) return null;
    return responseData.data.outputs as V1NodeExecutedOutputs;
  }, [responseData]);

  const { resolvedOutputs, hadOriginalOutputs } = useMemo(() => {
    if (!outputsData)
      return { resolvedOutputs: null, hadOriginalOutputs: false };
    const resolved = extractOutputsValue(outputsData);
    const hadOriginalOutputs = Object.keys(resolved).length > 0;
    return {
      resolvedOutputs: applyDisplayFilters(resolved).outputs,
      hadOriginalOutputs,
    };
  }, [outputsData]);

  const outputEntries = useMemo(
    () => (resolvedOutputs ? Object.entries(resolvedOutputs) : []),
    [resolvedOutputs],
  );

  const isAccessDeniedError = checkIsAccessDeniedError(executionOutputsError);

  /**
   * Handles expanding an output to full view.
   */
  const handleExpand = (name: string, value: TResolvedValue) => {
    setPreviewData({ name, value });
  };

  /**
   * Handles closing the preview layover.
   */
  const handleClose = () => {
    setPreviewData(null);
  };

  /**
   * Centralized download handler for all downloadable output types.
   */
  const handleDownload = useCallback((name: string, value: TResolvedValue) => {
    downloadOutput(value, name);
  }, []);

  // Show loading state
  if (isLoading) {
    return (
      <Flex align="center" justify="center" gap={2} className="py-4">
        <Spinner size="sm" />
        <Text color="muted" level="small">
          {t('chat.executionOutputs.loading')}
        </Text>
      </Flex>
    );
  }

  if (isError) {
    return isAccessDeniedError ? (
      <AccessDeniedAlert />
    ) : (
      <Alert variant="destructive">
        <AlertTitle>{t('chat.executionOutputs.error')}</AlertTitle>
        <AlertDescription>
          {t('chat.executionOutputs.errorDescription')}
        </AlertDescription>
      </Alert>
    );
  }

  // Show empty state
  const hasNoOutputs = outputEntries.length === 0;

  if (hasNoOutputs) {
    // Silent when the browser-action filter dropped everything.
    const filterDroppedEverything =
      hadOriginalOutputs &&
      resolvedOutputs !== null &&
      Object.keys(resolvedOutputs).length === 0;
    if (filterDroppedEverything) return null;
    return (
      <Flex align="center" justify="center" className="py-4">
        <Text color="muted" level="small">
          {t('chat.executionOutputs.empty')}
        </Text>
      </Flex>
    );
  }

  return (
    <>
      <Flex
        direction="column"
        gap={3}
        width="full"
        paddingY={2}
        className="min-w-0"
        data-testid="chat-output-cards"
      >
        {outputEntries.map(([key, value]) => (
          <OutputCard
            key={key}
            name={key}
            value={value}
            size={EOutputCardSize.LG}
            onExpand={handleExpand}
            onDownload={handleDownload}
          />
        ))}
      </Flex>

      {/* Preview Layover */}
      {previewData && (
        <PreviewLayover
          isOpen
          outputName={previewData.name}
          outputValue={previewData.value}
          onClose={handleClose}
          onDownload={downloadOutput}
        />
      )}
    </>
  );
}
